import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Gem, Loader, Users } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useWheel500Store from '../store/useWheel500Store'
import useAppStore from '../../../store/useAppStore'
import { WHEEL500_ZONE_CONFIG } from '../config/wheel500Config'
import { hapticNotify } from '../../../utils/haptic'

const formatFinishedAgo = (secondsValue, t) => {
  const totalSeconds = Math.max(0, Number(secondsValue || 0))

  if (totalSeconds < 60) {
    return t('wheel500.history.timeAgoSec', '{{count}} сек назад', { count: totalSeconds })
  }

  const minutes = Math.floor(totalSeconds / 60)
  if (minutes < 60) {
    return t('wheel500.history.timeAgoMin', '{{count}} мин назад', { count: minutes })
  }

  const hours = Math.floor(minutes / 60)
  return t('wheel500.history.timeAgoHour', '{{count}} ч назад', { count: hours })
}

const Wheel500History = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { loadHistory } = useWheel500Store()
  const isGuest = useAppStore((state) => state.isGuest)
  const openLogin = useAppStore((state) => state.openLogin)

  const [activeTab, setActiveTab] = useState('all')
  const [allRounds, setAllRounds] = useState([])
  const [myRounds, setMyRounds] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const rounds = activeTab === 'all' ? allRounds : myRounds

  useEffect(() => {
    let cancelled = false
    if (isGuest) {
      setActiveTab('all')
      setMyRounds([])
    }
    const loadData = async () => {
      setIsLoading(true)
      try {
        const data = await loadHistory()
        if (cancelled) return
        setAllRounds(data.all || [])
        setMyRounds(isGuest ? [] : data.mine || [])
      } catch (error) {
        if (cancelled || error?.isHandledAuthError) return
        hapticNotify('error')
        toast.error(t('wheel500.history.loadError', 'Не удалось загрузить историю'))
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadData()
    return () => { cancelled = true }
  }, [loadHistory, t, isGuest])

  return (
    <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8">
      <div className="mx-auto max-w-xl space-y-6">
        <div className="relative flex items-center justify-center lg:min-h-[48px]">
          <button
            onClick={() => navigate('/x50')}
            className="absolute left-0 inline-flex items-center justify-center rounded-xl border border-white/10 bg-surface p-2.5 text-white hover:text-accent-primary"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl lg:text-4xl font-bold text-white">{t('wheel500.history.title', 'История X50')}</h1>
        </div>

        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              activeTab === 'all' ? 'bg-accent-primary text-dark-primary' : 'bg-surface text-text-secondary'
            }`}
          >
            {t('wheel500.history.tabs.all', 'Все')}
          </button>
          <button
            onClick={() => isGuest ? openLogin() : setActiveTab('my')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              activeTab === 'my' ? 'bg-accent-primary text-dark-primary' : 'bg-surface text-text-secondary'
            }`}
          >
            {t('wheel500.history.tabs.my', 'Мои')}
          </button>
        </div>

        {isLoading ? (
          <div className="flex min-h-[300px] w-full items-center justify-center rounded-2xl border border-white/10 bg-surface/50">
            <Loader className="w-8 h-8 text-accent-primary animate-spin" />
          </div>
        ) : rounds.length === 0 ? (
          <div className="w-full rounded-2xl border border-white/10 bg-surface/50 p-8 text-center text-text-secondary">
            {t('wheel500.history.empty', 'История пуста')}
          </div>
        ) : (
          <div className="grid w-full grid-cols-1 gap-3">
            {rounds.map((round) => {
              const totalRoundSum = Number(round.sum_x2 || 0) + Number(round.sum_x3 || 0) + Number(round.sum_x5 || 0) + Number(round.sum_x50 || 0)
              const playersCount = Array.isArray(round.users)
                ? round.users.length
                : Array.isArray(round.bets)
                  ? round.bets.length
                  : 0
              const zoneKey = String(round.win_zone || '').toLowerCase()
              const zoneCfg = WHEEL500_ZONE_CONFIG[zoneKey]

              return (
                <button
                  key={round.id}
                  onClick={() => navigate(`/x50/history/${round.id}`)}
                  className="rounded-2xl border px-4 py-3 text-left transition-colors hover:border-white/25"
                  style={{
                    backgroundColor: `${zoneCfg?.hex || '#1a1a2e'}1a`,
                    borderColor: `${zoneCfg?.hex || '#ffffff'}40`
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-text-secondary">{formatFinishedAgo(round.finished_sec_ago, t)}</p>
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold border"
                      style={{
                        color: zoneCfg?.hex || '#FFFFFF',
                        borderColor: `${zoneCfg?.hex || '#FFFFFF'}66`,
                        backgroundColor: `${zoneCfg?.hex || '#FFFFFF'}1f`
                      }}
                    >
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: zoneCfg?.hex || '#fff' }} />
                      <span>{zoneCfg?.label || zoneKey}</span>
                    </span>
                  </div>

                  <div className="mt-2 flex items-center justify-between gap-3 text-sm">
                    <div className="inline-flex items-center gap-3">
                      <span className="inline-flex items-center gap-1.5 font-semibold text-white">
                        <Gem className="h-4 w-4 text-accent-primary" />
                        <span>{totalRoundSum.toLocaleString()}</span>
                      </span>

                      <span className="inline-flex items-center gap-1.5 text-text-secondary">
                        <Users className="h-4 w-4" />
                        <span>{playersCount}</span>
                      </span>
                    </div>

                    {!isGuest && round.you && (
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        round.you_win
                          ? 'border border-emerald-300/35 bg-emerald-500/15 text-emerald-100'
                          : 'border border-rose-300/35 bg-rose-500/15 text-rose-100'
                      }`}>
                        {round.you_win
                          ? t('wheel500.history.youWon', 'Выиграл')
                          : t('wheel500.history.youLost', 'Проиграл')}
                      </span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default Wheel500History
